'use client'

import React from 'react'
import classNames from 'classnames'
import { BackgroundGradient, GradientType } from '@/ui/atoms/BackgroundGradient'
import { Label } from '@/ui/atoms/Label'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { CMSButton } from '@/ui/components/CMSButton'
import RichText from '@/ui/components/RichText'
import type { Media, Hero } from '@/payload-types'
import { BatteryAnimation } from '@/ui/components/BatteryAnimation'
import { RootGutter } from '@/ui/layout/Gutter/new/page-layout'
import { MaxWidthGlobal } from '@/ui/layout/Gutter/new/MaxWidthGlobal'

interface LandingHeroProps {
  title?: string
  richText?: Hero['richText']
  iconTextFields?: Array<{
    label: string
  }>
  iconTextFieldsLabel?: string
  button?: Hero['buttons']
  buttonRichText?: Hero['richText']
  gradient?: GradientType
  image?: Media | string
  className?: string
}

export const LandingHero: React.FC<LandingHeroProps> = ({
  title,
  richText,
  iconTextFields = [],
  iconTextFieldsLabel,
  button,
  buttonRichText,
  gradient = GradientType.Gradient1,
  image,
  className,
}) => {
  return (
    <section
      className={classNames(
        'relative lg:pt-[200px] pt-[160px] lg:pb-stack pb-mobile-stack',
        className,
      )}
    >
      {/* Background Image and Gradient */}
      <BackgroundGradient type={gradient} className="absolute inset-0" />
      {image && typeof image === 'object' && image.url && (
        <CMSImage
          resource={image}
          className="absolute inset-0 w-full h-full object-cover -z-20"
          priority
          fill
        />
      )}

      {/* Content */}
      <RootGutter className={'[&>*]:!col-start-3 [&>*]:!col-end-4'}>
        <MaxWidthGlobal>
          <div className="relative z-10 mx-auto">
            <div className="grid grid-cols-1 gap-10 xl:grid-cols-2 items-start justify-start">
              <div className="">
                {/* Title. Style guide release (NEXT_PUBLIC_STYLE_GUIDE, R8): with the bold Figma headline
                    the title breaks "Know-How" at its hyphen on 390 px phones, so below sm it takes
                    headline-L (docs/style-guide/README.md 2.2) */}
                {title && (
                  <h1
                    className={classNames(
                      process.env.NEXT_PUBLIC_STYLE_GUIDE === '1'
                        ? 'text-headline-L-semibold sm:text-headline-XL-semibold'
                        : 'text-headline-XL-semibold',
                      'text-text-inverted mb-6 leading-[120%]',
                    )}
                  >
                    {title}
                  </h1>
                )}

                {/* Description (RichText) */}
                {richText && (
                  <div className="mb-8">
                    <RichText
                      data={richText}
                      className="text-text-subtle-inverted prose-p:!mb-0 prose-strong:text-text-subtle-inverted prose-em:text-text-subtle-inverted prose-a:text-text-subtle-inverted"
                      enableGutter={false}
                    />
                  </div>
                )}

                {/* Icon with Text Fields */}
                {iconTextFields && iconTextFields.length > 0 && (
                  <div className="mb-8">
                    {iconTextFieldsLabel && (
                      <p className="text-label-XS-regular text-text-subtle-inverted mb-2">
                        {iconTextFieldsLabel}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {iconTextFields.map((field, index) => (
                        <Label
                          key={index}
                          title={field.label}
                          variant="transparentInverted"
                          size="medium"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Section - Description (RichText) and Button */}
                {(button || buttonRichText) && (
                  <div>
                    {(button || buttonRichText) && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {button && button.length > 0 && (
                          <>
                            {button.map((btn, index) => (
                              <CMSButton
                                key={index}
                                {...btn.button}
                                size="md"
                                appearance={'primaryInverted'}
                              />
                            ))}
                          </>
                        )}
                        {buttonRichText && (
                          <div className="text-white">
                            <RichText
                              data={buttonRichText}
                              className="prose-p:!mb-0 text-body-S-regular prose-strong:text-white prose-em:text-white prose-a:text-white"
                              enableGutter={false}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Battery Animation */}
              <div className="hidden lg:flex justify-center items-center overflow-visible">
                <BatteryAnimation
                  imageUrl="/battery-original.png"
                  className="w-full max-w-[500px] xl:max-w-[650px]"
                  width={800}
                  height={600}
                  particleGap={3}
                  noise={5}
                  mouseForce={30}
                  gravity={0.08}
                />
              </div>
            </div>
          </div>
        </MaxWidthGlobal>
      </RootGutter>
    </section>
  )
}
